/**
 * ProvisionDashboard — Startseite für Provisionspartner (role="provision").
 *
 * „Auftrag einreichen" öffnet die Vollbild-Erfassung (/provision/erfassen/:id)
 * mit Bottom-Navigation + Kamera — wie im Case Editor der Webapp.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Camera, Loader2, Plus, CheckCircle2, Clock, FileText, Banknote,
  Save, Wallet, Calculator, TrendingUp, Rocket, PenLine, ChevronRight,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const fmtEUR = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v) || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");

const STATUS_META = {
  draft: { label: "Entwurf", cls: "bg-zinc-100 text-zinc-600 border-zinc-300", icon: PenLine },
  submitted: { label: "Eingereicht", cls: "bg-sky-50 text-sky-700 border-sky-200", icon: Clock },
  in_progress: { label: "In Bearbeitung", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Loader2 },
  report_done: { label: "Gutachten fertig", cls: "bg-violet-50 text-violet-700 border-violet-200", icon: FileText },
  paid: { label: "Ausgezahlt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.submitted;
  return (
    <Badge variant="outline" className={`${m.cls} text-[11px] font-bold`} data-testid={`order-status-${status}`}>
      {m.label}
    </Badge>
  );
}

const INDEPENDENT_AVG = 750;

function ComparisonCalculator({ commission, monthOrders }) {
  const [count, setCount] = useState(5);
  const partnerIncome = count * commission;
  const independentIncome = count * INDEPENDENT_AVG;
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="provision-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="h-4 w-4 text-cyan-700" />
        <span className="font-display font-bold">Vergleichsrechner</span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Was verdienst du als Partner — und was wäre als selbstständiger Gutachter möglich?
      </p>
      <div className="max-w-xl">
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs">Gutachten pro Monat</Label>
          <span className="text-sm font-bold tabular-nums text-cyan-700" data-testid="calculator-count">{count}</span>
        </div>
        <input
          type="range"
          min="1"
          max="20"
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          className="w-full accent-cyan-600"
          data-testid="calculator-slider"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 max-w-xl">
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4" data-testid="calculator-partner-box">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">Als Partner</div>
          <div className="text-2xl font-display font-bold mt-1 tabular-nums">{fmtEUR(partnerIncome)}</div>
          <div className="text-[11px] text-zinc-400 mt-0.5">{count} × {fmtEUR(commission)} Provision</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="calculator-independent-box">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-600">
            <TrendingUp className="h-3 w-3" /> Als Selbstständiger
          </div>
          <div className="text-2xl font-display font-bold mt-1 tabular-nums text-emerald-700">ca. {fmtEUR(independentIncome)}</div>
          <div className="text-[11px] text-emerald-600/70 mt-0.5">{count} × ca. {fmtEUR(INDEPENDENT_AVG)} abzgl. Software-Kosten</div>
        </div>
      </div>
      {count > 8 && (
        <div className="mt-3 max-w-xl text-xs text-emerald-700 font-semibold" data-testid="calculator-hint">
          Bei {count} Gutachten/Monat entgehen dir als Partner ca. {fmtEUR(independentIncome - partnerIncome)} — Selbstständigkeit lohnt sich!
        </div>
      )}
      {monthOrders > 8 && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="calculator-upgrade-banner">
          <Rocket className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-sm text-amber-800">
              Stark! {monthOrders} Aufträge diesen Monat 🚀
            </div>
            <div className="text-xs text-amber-700 mt-1">
              Als selbstständiger Gutachter hättest du damit ca. <strong>{fmtEUR(monthOrders * INDEPENDENT_AVG)}</strong> statt{" "}
              <strong>{fmtEUR(monthOrders * commission)}</strong> verdient. Sprich uns an — wir helfen dir beim Umstieg
              in die Selbstständigkeit mit easygutachter.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProvisionDashboard() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [iban, setIban] = useState(user?.payout_iban || "");
  const [ibanSaving, setIbanSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: o }, { data: s }] = await Promise.all([
        api.get("/partner-orders"),
        api.get("/partner-orders/summary"),
      ]);
      setOrders(o.orders || []);
      setSummary(s);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setIban(user?.payout_iban || ""); }, [user?.payout_iban]);

  const startOrder = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/partner-orders", { draft: true });
      navigate(`/provision/erfassen/${data.order.id}`);
    } catch (e) {
      toast.error(apiError(e, "Auftrag konnte nicht angelegt werden"));
      setCreating(false);
    }
  };

  const openOrder = (o) => {
    if (o.case_id || ["report_done", "paid"].includes(o.status)) return;
    navigate(`/provision/erfassen/${o.id}`);
  };

  const saveIban = async () => {
    setIbanSaving(true);
    try {
      await api.patch("/users/me/profile", { payout_iban: iban.trim() });
      await refreshMe?.();
      toast.success("IBAN gespeichert — Auszahlungen gehen auf dieses Konto.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setIbanSaving(false);
    }
  };

  return (
    <Layout
      title="Meine Aufträge"
      subtitle="Fotos hochladen — wir erledigen den Rest."
      primaryAction={{
        label: "Auftrag",
        icon: Plus,
        onClick: startOrder,
        disabled: creating,
        testid: "mobile-fab-new-order",
      }}
    >
      <div className="space-y-6" data-testid="provision-dashboard">
        {/* ── Provisions-Übersicht ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="provision-kpi-total">
            <div className="flex items-center gap-2 text-zinc-400"><FileText className="h-4 w-4" /><span className="text-[10px] uppercase tracking-wider font-semibold">Aufträge gesamt</span></div>
            <div className="text-3xl font-display font-bold mt-2 tabular-nums">{summary?.total_orders ?? "—"}</div>
          </div>
          <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="provision-kpi-open">
            <div className="flex items-center gap-2 text-amber-500"><Clock className="h-4 w-4" /><span className="text-[10px] uppercase tracking-wider font-semibold">Offene Provision</span></div>
            <div className="text-3xl font-display font-bold mt-2 tabular-nums">{summary ? fmtEUR(summary.open_amount) : "—"}</div>
            <div className="text-[11px] text-zinc-400 mt-0.5">Auszahlung nach Zahlungseingang der Versicherung</div>
          </div>
          <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="provision-kpi-paid">
            <div className="flex items-center gap-2 text-emerald-600"><Banknote className="h-4 w-4" /><span className="text-[10px] uppercase tracking-wider font-semibold">Ausgezahlt</span></div>
            <div className="text-3xl font-display font-bold mt-2 tabular-nums">{summary ? fmtEUR(summary.paid_amount) : "—"}</div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="rounded-2xl border-2 border-dashed border-cyan-300 bg-cyan-50/40 p-6 text-center">
          <div className="font-display font-bold text-lg">Neuer Schadenfall?</div>
          <div className="text-sm text-zinc-500 mt-1">
            Kundendaten, Fotos & Unterschrift direkt vor Ort erfassen — Schritt für Schritt.
            Du bekommst <strong>{fmtEUR(summary?.default_commission ?? 200)}</strong> pro Auftrag.
          </div>
          <Button
            size="lg"
            className="mt-4 brand-gradient-bg text-white px-8"
            onClick={startOrder}
            disabled={creating}
            data-testid="provision-new-order-btn"
          >
            {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Auftrag einreichen
          </Button>
        </div>

        {/* ── Auftragsliste ── */}
        <div className="rounded-2xl border border-zinc-200 bg-white" data-testid="provision-orders-list">
          <div className="px-5 py-4 border-b border-zinc-100 font-display font-bold">Meine Aufträge</div>
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-300" /></div>
          ) : orders.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-400" data-testid="provision-orders-empty">
              Noch keine Aufträge — reiche jetzt deinen ersten ein!
            </div>
          ) : (
            <div className="divide-y divide-zinc-50">
              {orders.map((o) => {
                const editable = !o.case_id && !["report_done", "paid"].includes(o.status);
                return (
                  <div
                    key={o.id}
                    className={`px-5 py-3.5 flex items-center gap-4 flex-wrap ${editable ? "cursor-pointer hover:bg-zinc-50/70 transition" : ""}`}
                    onClick={() => openOrder(o)}
                    data-testid={`provision-order-${o.id}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-zinc-100 text-zinc-500 flex items-center justify-center shrink-0">
                      <Camera className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-semibold text-sm text-zinc-900">
                        {[o.customer_firstname, o.customer_name].filter(Boolean).join(" ") || "Ohne Namen"}
                        {o.license_plate ? <span className="text-zinc-400 font-normal"> · {o.license_plate}</span> : null}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {o.status === "draft" ? "Entwurf" : `Eingereicht ${fmtDate(o.created_at)}`} · {(o.photos || []).length} Foto(s)
                      </div>
                    </div>
                    <div className="text-sm font-bold tabular-nums text-zinc-700">{fmtEUR(o.commission_amount)}</div>
                    <StatusBadge status={o.status} />
                    {editable && <ChevronRight className="h-4 w-4 text-zinc-300" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Vergleichsrechner ── */}
        <ComparisonCalculator
          commission={Number(summary?.default_commission ?? 200)}
          monthOrders={Number(summary?.month_orders ?? 0)}
        />

        {/* ── IBAN ── */}
        <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="provision-iban-card">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="h-4 w-4 text-cyan-700" />
            <span className="font-display font-bold">Auszahlungskonto</span>
          </div>
          <p className="text-xs text-zinc-500 mb-3">Auf dieses Konto überweisen wir deine Provisionen.</p>
          <div className="flex gap-2 max-w-lg">
            <Input
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              placeholder="DE00 0000 0000 0000 0000 00"
              className="font-mono"
              data-testid="provision-iban-input"
            />
            <Button onClick={saveIban} disabled={ibanSaving} data-testid="provision-iban-save-btn">
              {ibanSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
